import { useEffect, useState } from "react";
import TopBar from "../../../common/topbar/TopBar";
import StatusCards, { ResignedEmployeeStats } from "./StatusCards";
import {
  FilterCardItem,
  RoleEnum,
  statusEnum,
} from "../../../../types/common-types";
import PageLoader from "../../../common/loader/PageLoader";
import ResignedEmployeeTable from "./ResignedEmployeeTable";
import {
  getResignedEmployeeCount,
  getResignedEmployees,
  updateResignedEmployeeStatus,
} from "../../../../apis/workforce/resigned.api";
import Pagination from "../../../common/pagination/Pagination";
import { acceptStatusOptions } from "../../../../constants/constants";
import Button from "../../../common/button/Button";
import ApplyResignation from "./apply-resignation";
import { useAuthStore } from "../../../../store/auth-store";
import StatusUpdateModal from "./StatusUpdate";

export interface ResignationRequest {
  _id: string;
  companyId: string;
  userId: ResignationUser;
  lastWorkingDate: string;
  mailSent: boolean;
  reason: string;
  status: statusEnum;
  createdAt: string;
  updatedAt: string;
}

export interface ResignationUser {
  _id: string;
  firstName: string;
  lastName: string;
  profileImage: string;
  role: RoleEnum;
  departmentId: IBaseEntity;
  designationId?: IBaseEntity;
  branchId?: IBaseEntity;
  shiftId?: IShift;
  userId: string;
}

export interface IShift {
  _id: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface IBaseEntity {
  _id: string;
  name: string;
}

export const initialEmployee: ResignationRequest = {
  _id: "",
  companyId: "",
  userId: {
    _id: "",
    firstName: "",
    lastName: "",
    profileImage: "",
    role: RoleEnum.EMPLOYEE,
    departmentId: {
      _id: "",
      name: "",
    },
    userId: "",
  },
  lastWorkingDate: "",
  mailSent: false,
  reason: "",
  status: statusEnum.REJECTED,
  createdAt: "",
  updatedAt: "",
};

const ResignedEmployees = () => {
  const { user } = useAuthStore();
  const isManager = user?.role === RoleEnum.MANAGER;
  const isEmployee = user?.role === RoleEnum.EMPLOYEE;
  const [activeCard, setActiveCard] = useState<string>("");
  const [statusOpen, setStatusOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusLoading, setStatusLoading] = useState<boolean>(false);

  const [resignedEmployees, setResignedEmployees] = useState<
    ResignationRequest[]
  >([]);

  const [employeeDetails, setEmployeeDetails] =
    useState<ResignationRequest>(initialEmployee);

  const [show, setShow] = useState<boolean>(false);
  const [resignationId, setResignationId] = useState<string>("");

  const [cards, setCards] = useState<FilterCardItem[]>([
    {
      id: "",
      title: "Total",
      count: 0,
      activeColor: "bg-info",
      textColor: "text-info",
      icon: <i className="fa-solid fa-align-justify"></i>,
    },
    {
      id: statusEnum.PENDING,
      title: "Pending",
      count: 0,
      activeColor: "bg-pending",
      textColor: "text-pending",
      icon: <i className="fa-solid fa-hourglass-end"></i>,
    },
    {
      id: statusEnum.ACCEPTED,
      title: "Accepted",
      count: 0,
      activeColor: "bg-success",
      textColor: "text-success",
      icon: <i className="fa-solid fa-user-check"></i>,
    },
    {
      id: statusEnum.REJECTED,
      title: "Rejected",
      count: 0,
      activeColor: "bg-danger",
      textColor: "text-danger",
      icon: <i className="fa-solid fa-user-times"></i>,
    },
  ]);

  useEffect(() => {
    getEmployeeCounts();
    // eslint-disable-next-line
  }, []);

  const getEmployeeCounts = async () => {
    const response = await getResignedEmployeeCount();
    if (response?.success) {
      updateCards(response?.data);
    }
  };

  // update cards
  const updateCards = (stats: ResignedEmployeeStats) => {
    setCards((prev) =>
      prev.map((card) => {
        switch (card.id) {
          case "":
            return { ...card, count: stats.total };

          case statusEnum.ACCEPTED:
            return { ...card, count: stats.accept };

          case statusEnum.REJECTED:
            return { ...card, count: stats.reject };

          case statusEnum.PENDING:
            return { ...card, count: stats.pending };

          default:
            return card;
        }
      }),
    );
  };

  // useEffect for get employeeDetails
  useEffect(() => {
    fetchResignedEmployeeList(page, limit, search, activeCard);
    // eslint-disable-next-line
  }, [page, limit, search, activeCard]);

  // get employeeDetails list
  const fetchResignedEmployeeList = async (
    page: number,
    limit: number,
    search: string = "",
    status: string = "",
  ) => {
    setLoading(true);
    const response = await getResignedEmployees({
      page,
      limit,
      search,
      status,
    });
    if (response.success && response.data?.resignations?.length > 0) {
      setResignedEmployees(response.data?.resignations);
      setTotal(response.data?.total);
      setLoading(false);
    } else {
      setResignedEmployees([]);
      setTotal(0);
      setLoading(false);
    }
  };

  const handleRefreshData = () => {
    fetchResignedEmployeeList(page, limit, search, activeCard);
    getEmployeeCounts();
  };

  // handle status open close
  const handleStatusOpenClose = () => {
    setStatusOpen((prev) => !prev);
    setEmployeeDetails(initialEmployee);
  };

  // handle update status
  const handleUpdateStatus = (
    employeeDetails: ResignationRequest,
    type: "status" | "update",
  ) => {
    if (type === "status") {
      handleStatusOpenClose();
      setEmployeeDetails(employeeDetails);
    } else {
      handleOpenClose(employeeDetails);
    }
  };

  const handleStatusSubmit = async (formData: {
    status: statusEnum;
    lastWorkingDate?: string;
    remarks: string;
  }) => {
    setStatusLoading(true);

    const payload = {
      status: formData.status.trim(),
      lastWorkingDate: formData.lastWorkingDate,
      remarks: formData.remarks,
    };

    const response = await updateResignedEmployeeStatus(
      payload,
      employeeDetails._id,
    );
    if (response.success) {
      handleRefreshData();
    }
    setStatusLoading(false);
  };

  // handle search employeeDetails
  const handleOnSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // handle Download Excel
  const handleDownloadExcel = async (password: string) => {
    await getResignedEmployees({
      page,
      limit,
      search,
      status: "",
      isDownload: true,
      password,
    });
  };

  // handle Apply resignation open close
  const handleOpenClose = (resignation?: ResignationRequest) => {
    setShow((prev) => !prev);
    if (resignation) {
      setResignationId(resignation._id);
    } else {
      setResignationId("");
    }
  };

  return (
    <>
      <TopBar
        title={isEmployee ? "Resignation" : "All Resigned Employee"}
        isSearch={!isEmployee}
        searchPlaceholder="Search employees..."
        onSearch={handleOnSearch}
        isExcel
        actionButtons={
          isEmployee || isManager ? (
            <Button
              name="Add New"
              size="sm"
              onClick={() => handleOpenClose()}
              leftIcon={<i className="fa-solid fa-plus"></i>}
            />
          ) : null
        }
        handleDownloadExcel={handleDownloadExcel}
      />
      <div className="content-area flex flex-col gap-3">
        <PageLoader loading={loading} />
        <StatusCards
          cards={cards}
          activeCard={activeCard}
          setActiveCard={setActiveCard}
        />
        <ResignedEmployeeTable
          resignedEmployees={resignedEmployees}
          handleUpdateStatus={handleUpdateStatus}
          refreshData={() =>
            fetchResignedEmployeeList(page, limit, search, activeCard)
          }
        />
        <Pagination
          totalRecords={total}
          currentPage={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
        />
      </div>
      <StatusUpdateModal
        title={`employee ${employeeDetails.userId.firstName} ${employeeDetails.userId.lastName}`}
        isOpen={statusOpen}
        status={employeeDetails.status}
        handleOpenClose={handleStatusOpenClose}
        handleSubmit={handleStatusSubmit}
        loading={statusLoading}
        options={acceptStatusOptions}
      />
      <ApplyResignation
        show={show}
        handleOpenClose={handleOpenClose}
        resignationId={resignationId}
      />
    </>
  );
};

export default ResignedEmployees;
